import { IMarginFee, IRawEnergyPriceProjectionComponent } from '@schemas/scenarios-simulator/types';
import { createScenariosSimulatorApiClient } from './api-client';
import { ModalityTypeEnum, ProductTypeEnum, EnergyTypeEnum, RiskTypeEnum } from '@schemas/scenarios-simulator/enums';
import { normalizeProjected } from '@utils/scenarios-simulator/normalize-projections';

/**
 * Energy price enums and types
 * Note: Enums are now imported from centralized enums.ts to avoid duplication
 */

export interface GetEnergyPricesParams {
  product_type: ProductTypeEnum;
  modality_type?: ModalityTypeEnum;
  supply_start_date: string;
  supply_final_date: string;
  energy_type: EnergyTypeEnum;
  state: string;
  risk_type?: RiskTypeEnum;
  edc_id?: string | number;
}

export interface EnergyPricesResponse {
  data: {
    raw_energy_price_list: Record<string, any>;
    origin: string;
    submarket: string;
    energy_type: string;
    margin_fee: Record<string, any>;
  };
}

/**
 * Fetches energy prices and margins
 *
 * @param params - Energy price request parameters
 * @param token - Bearer authentication token
 * @returns Promise with energy prices and margin fee
 */
export async function getEnergyPricesAndMargins(
  params: GetEnergyPricesParams,
  token?: string,
): Promise<{
  energyPriceInfo: { origin: string };
  rawEnergyPrice: IRawEnergyPriceProjectionComponent;
  marginFee: IMarginFee;
}> {
  const apiClient = createScenariosSimulatorApiClient(token);

  const payload: Record<string, any> = {
    product_type: params.product_type,
    supply_start_date: params.supply_start_date,
    supply_final_date: params.supply_final_date,
    energy_type: params.energy_type,
    state: params.state,
  };

  if (params.modality_type !== undefined && params.modality_type !== null) {
    payload.modality_type = params.modality_type;
  }

  if (params.risk_type !== undefined && params.risk_type !== null) {
    payload.risk_type = params.risk_type;
  }

  if (params.edc_id !== undefined && params.edc_id !== null) {
    payload.edc_id = Number(params.edc_id);
  }

  const { data: response } = await apiClient.post<EnergyPricesResponse>('/v1/energy-prices', payload);
  const { data } = response;
  return {
    energyPriceInfo: { origin: data.origin },
    rawEnergyPrice: {
      rawPriceList: normalizeProjected(data.raw_energy_price_list),
      energyType: data.energy_type,
      submarket: data.submarket,
      origin: data.origin,
    } as unknown as IRawEnergyPriceProjectionComponent,
    marginFee: {
      type: 'ABSOLUTE',
      aux__useSamePricePerYears: false,
      values: normalizeProjected(data.margin_fee) as Record<string, number>,
    },
  };
}

export { ProductTypeEnum, ModalityTypeEnum, EnergyTypeEnum, RiskTypeEnum };
